import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  Firestore,
  arrayRemove, arrayUnion, deleteField, doc, getDoc, serverTimestamp, updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { Account } from '../models/account.model';
import { Organization } from '../models/organization.model';
import { AccountService } from './account.service';
import { AuthBridgeService } from './auth-bridge.service';
import { DataStoreService } from './data-store.service';
import { FirebaseService } from './firebase.service';
import { IntegrationService } from './integration.service';
import { ProductService } from './product.service';
import { UserService } from './user.service';

interface OrganizationData {
  name: string;
  ownerId: string;
  createdAt: any;
  licenseAccepted: boolean;
  acceptedBy?: string;
  acceptedAt?: any;
  licenseToken?: string;
}

type CreateOrganizationOptions = {
  licenseAccepted?: boolean;
  generateLicenseToken?: boolean;
  microsoftSync?: {
    microsoftTenantId: string;
    syncEnabled: boolean;
  };
};

export enum OrganizationErrorCodes {
  OrganizationNotFound = 'organization-not-found',
  OrganizationUnauthorized = 'organization-unauthorized',
  UserNotAuthenticated = 'user-not-authenticated',
  FailToCreateOrganization = 'fail-to-create-organization',
}

export class OrganizationError extends Error {
  constructor(readonly code: OrganizationErrorCodes) {
    super(code);
    this.name = 'OrganizationError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly firebase = inject(FirebaseService);
  private readonly dataStore = inject(DataStoreService);
  private readonly userService = inject(UserService);
  private readonly accountService = inject(AccountService);
  private readonly productService = inject(ProductService);
  private readonly integrationService = inject(IntegrationService);
  private readonly authBridge = inject(AuthBridgeService);
  private readonly router = inject(Router);

  async getOrganization(
    organizationId: string,
    updateCurrent = false,
    knownOrganizationIds?: string[],
    // Data residency: Firestore of the org's regional project, when the caller
    // resolved it (loading every org of the user). Defaults to the active org's.
    db: Firestore = this.firebase.firestore,
  ): Promise<Organization> {
    const current = this.dataStore.getProperty('currentOrganization');
    if (current && current.id === organizationId && !updateCurrent) {
      return current;
    }

    // Verify user is authenticated
    const currentUser = this.firebase.auth.currentUser;
    if (!currentUser) {
      throw new OrganizationError(OrganizationErrorCodes.UserNotAuthenticated);
    }

    // Verify user has access to this organization. Callers that already hold
    // the user's org list (e.g. OrgContext loading every org at once) pass it
    // in so we skip a redundant user-doc read per org.
    let organizationIds = knownOrganizationIds;
    if (!organizationIds) {
      const userDoc = await getDoc(doc(this.firebase.globalFirestore, 'users', currentUser.uid));
      organizationIds = (userDoc.data()?.['organizationIds'] as string[]) || [];
    }

    if (!organizationIds.includes(organizationId)) {
      throw new OrganizationError(OrganizationErrorCodes.OrganizationUnauthorized);
    }

    const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
    if (!orgDoc.exists()) {
      throw new OrganizationError(OrganizationErrorCodes.OrganizationNotFound);
    }

    const data = orgDoc.data();

    // Auto-migrate legacy single-tenant field to tids array. Derive `tids`
    // in-memory unconditionally so callers get the right shape; persisting the
    // migration is best-effort. Only org admins/owners can write the org doc,
    // so a plain member loading a still-unmigrated org would otherwise hit
    // permission-denied here — which, on the bootstrap path, blanks the whole
    // app. An admin load (or backend) will persist it later.
    if (data['tid'] && !data['tids']?.length) {
      data['tids'] = [data['tid']];
      try {
        const orgRef = doc(db, 'organizations', organizationId);
        await updateDoc(orgRef, { tids: [data['tid']], tid: deleteField() });
      } catch {
        // Member without write access — non-fatal; tids is already set above.
      }
    }

    const organization: Organization = {
      id: organizationId,
      name: data['name'],
      ownerId: data['ownerId'],
      createdAt: data['createdAt'],
      licenseAccepted: data['licenseAccepted'],
      licenseToken: data['licenseToken'],
      awsFulfillment: data['awsFulfillment'],
      stripeCustomerId: data['stripeCustomerId'],
      creditBalance: data['creditBalance'],
      tids: data['tids'] ?? [],
      accounts: data['accounts'],
      milestones: data['milestones'],
      onboardingProgram: data['onboardingProgram'],
      isPartner: data['isPartner'],
      partnerId: data['partnerId'],
    };

    if (updateCurrent) {
      this.dataStore.updateState({ currentOrganization: organization });
    }

    return organization;
  }

  async createOrganization(
    organizationName: string,
    options: Partial<CreateOrganizationOptions> = {},
  ): Promise<{ organization: Organization; accounts: Account[] }> {
    const { licenseAccepted = false, generateLicenseToken = false, microsoftSync } = options;
    const ownerId = this.firebase.auth.currentUser?.uid;
    if (!ownerId) {
      throw new OrganizationError(OrganizationErrorCodes.UserNotAuthenticated);
    }
    return this.createOrganizationWithOwner(ownerId, organizationName, licenseAccepted, generateLicenseToken, microsoftSync);
  }

  async createOrganizationWithOwner(
    userId: string,
    organizationName: string,
    licenseAccepted = false,
    generateLicenseToken = false,
    microsoftSync?: { microsoftTenantId: string; syncEnabled: boolean }
  ): Promise<{ organization: Organization; accounts: Account[] }> {
    const callerUid = this.firebase.auth.currentUser?.uid;
    if (!callerUid || callerUid !== userId) {
      throw new OrganizationError(OrganizationErrorCodes.UserNotAuthenticated);
    }

    let result: { organizationId: string; creditsGranted: number; licenseToken: string | null };
    try {
      const callable = httpsCallable<
        {
          name: string;
          licenseAccepted: boolean;
          generateLicenseToken: boolean;
          microsoftSync?: { microsoftTenantId: string; syncEnabled: boolean };
        },
        { organizationId: string; creditsGranted: number; licenseToken: string | null }
      >(this.firebase.globalFunctions, 'createOrganization'); // global step: writes orgDirectory, creates in-region
      const resp = await callable({
        name: organizationName,
        licenseAccepted,
        generateLicenseToken,
        microsoftSync,
      });
      result = resp.data;
    } catch (e) {
      console.error('Creating organization - Callable failed:', e);
      throw new OrganizationError(OrganizationErrorCodes.FailToCreateOrganization);
    }

    // Data residency: seed the NEW org in its own regional project (it may not
    // be the active org's region), then restore the active org's binding.
    const restoreBinding = this.firebase.captureBinding();
    try {
      const connection = await this.authBridge.bridge.connectRegion({
        orgId: result.organizationId,
        regionalConfigs: this.firebase.regionalConfigs,
        firestore: this.firebase.globalFirestore,
      });
      this.firebase.bindOrg(result.organizationId, connection.region, connection.app);
      await this.productService.saveProductAccessConfig(result.organizationId, {
        enabledProducts: ['connect', 'chat'],
        roleProductAccess: {},
      });
      // Seed integrations that don't require any setup (authType: 'none') so
      // new orgs have working web + AI search out of the box. Other paid /
      // OAuth-gated integrations stay off and require explicit opt-in.
      await this.integrationService.saveFullConfig(result.organizationId, {
        integrations: {
          'google-search': { enabled: true, connectionMode: 'organization', roleAccess: [], allowedTools: [] },
          'perplexity':    { enabled: true, connectionMode: 'organization', roleAccess: [], allowedTools: [] },
        },
      });
      await Promise.all([
        this.productService.ensureProductAccount(result.organizationId, 'connect'),
        this.productService.ensureProductAccount(result.organizationId, 'chat'),
        this.productService.ensureProductAccount(result.organizationId, 'admin'),
      ]);
    } finally {
      restoreBinding();
    }

    const organization: Organization = {
      id: result.organizationId,
      name: organizationName,
      ownerId: userId,
      createdAt: serverTimestamp(),
      licenseAccepted,
      licenseToken: result.licenseToken ?? undefined,
      tids: microsoftSync?.syncEnabled && microsoftSync.microsoftTenantId ? [microsoftSync.microsoftTenantId] : [],
      creditBalance: result.creditsGranted,
      accounts: {},
    };

    return { organization, accounts: [] };
  }

  async renameOrganization(organizationId: string, newName: string): Promise<Organization> {
    const organizationRef = doc(this.firebase.firestore, 'organizations', organizationId);
    await updateDoc(organizationRef, { name: newName });

    const current = this.dataStore.getProperty('currentOrganization');
    if (current && current.id === organizationId) {
      const updated = { ...current, name: newName };
      this.dataStore.updateState({ currentOrganization: updated });
      return updated;
    }

    return this.getOrganization(organizationId, true);
  }

  async linkAccountToOrganization(accountId: string, organizationId: string) {
    const accountRef = this.accountService.getAccountRef(accountId);
    await updateDoc(accountRef, { organizationId });
  }

  async isOrganizationOwner(organizationId: string, userId: string): Promise<boolean> {
    const organization = await this.getOrganization(organizationId);
    return organization.ownerId === userId;
  }

  async addOrganizationToUser(userId: string, organizationId: string) {
    await this.userService.addOrganizationToUser(userId, organizationId);
  }

  async isLicenseAccepted(organizationId: string): Promise<boolean> {
    const organization = await this.getOrganization(organizationId);
    return !!organization.licenseAccepted;
  }

  async verifyLicenseToken(organizationId: string, token: string): Promise<boolean> {
    const callable = httpsCallable<
      { organizationId: string; token: string },
      { verified: boolean }
    >(this.firebase.functions, 'verifyLicenseTokenFromOrganization');
    const result = await callable({ organizationId, token });
    return result.data.verified;
  }

  async acceptLicense(organizationId: string, token: string): Promise<boolean> {
    const callable = httpsCallable<
      { organizationId: string; token: string },
      { success: boolean }
    >(this.firebase.functions, 'acceptLicenseForOrganization');
    const result = await callable({ organizationId, token });
    return result.data.success;
  }

  async acceptLicenseAsAdmin(organizationId: string, acceptedBy: string) {
    const organizationRef = doc(this.firebase.firestore, 'organizations', organizationId);
    await updateDoc(organizationRef, {
      licenseAccepted: true,
      acceptedBy,
      acceptedAt: serverTimestamp(),
    });
  }

  async clearOrganizationAccountFromUser(organizationId: string) {
    const currentAccount = this.dataStore.getProperty('currentAccount');
    if (currentAccount && currentAccount.organizationId === organizationId) {
      const currentUser = this.dataStore.getProperty('currentUser');
      const accountId = currentUser?.defaultAccountId;
      if (accountId) {
        await this.accountService.setCurrentAccount(accountId);
      } else {
        await this.accountService.deleteCurrentAccountInSession();
      }
    }
    this.router.navigate(['/home']);
  }

  async connectMicrosoftTenant(organizationId: string, tenantId: string): Promise<void> {
    const orgRef = doc(this.firebase.firestore, 'organizations', organizationId);
    await updateDoc(orgRef, { tids: arrayUnion(tenantId) });
  }

  async disconnectMicrosoftTenant(organizationId: string, tenantId: string): Promise<void> {
    const orgRef = doc(this.firebase.firestore, 'organizations', organizationId);
    await updateDoc(orgRef, { tids: arrayRemove(tenantId) });
  }

  /** @param tenantId Entra tid captured from the Microsoft sign-in response.
   *  When provided, the backend auto-discovers orgs linked to that tenant
   *  and joins the user to them.
   *  @param microsoftUserId The user's Entra OID claim (NOT Firebase's
   *  providerData.uid, which is the per-app `sub`). Lets the backend reach
   *  the right principal in Microsoft Graph. */
  async syncMicrosoftRoleOnLogin(tenantId?: string, microsoftUserId?: string): Promise<void> {
    const fn = httpsCallable<
      { tenantId?: string; microsoftUserId?: string },
      { synced: boolean }
    >(this.firebase.functions, 'syncMicrosoftRoleOnLogin');
    await fn({ tenantId, microsoftUserId });
  }

  async detachTenantUsers(organizationId: string, tenantId: string): Promise<void> {
    const fn = httpsCallable<{ organizationId: string; tenantId: string }, { detached: number }>(
      this.firebase.functions, 'detachTenantUsers'
    );
    await fn({ organizationId, tenantId });
  }

}
